// @flow
/* eslint-disable no-console */

import Polyglot, {
  type onMissingKeyType,
  default as PolyglotType,
} from 'node-polyglot';

import { localize as pseudolocalize } from 'pseudo-localization';

window.Greenhouse = window.Greenhouse || {};
window.Greenhouse.Translation = window.Greenhouse.Translation || {};
window.Greenhouse.Translation.fallbackTranslations =
  window.Greenhouse.Translation.fallbackTranslations || {};
window.Greenhouse.Translation.translations =
  window.Greenhouse.Translation.translations || {};

type LocalizeOpts = { [key: string]: any };

type PolyglotOpts = {
  phrases?: { [key: string]: string },
  locale?: string,
  allowMissing?: boolean,
  onMissingKey?: (key: string, opts: PolyglotOpts, locale: string) => string,
  interpolation?: { [key: string]: string },
  pluralRules?: { [key: string]: any },
};

const polyglot: PolyglotType = new Polyglot({
  onMissingKey: (key: string, opts: PolyglotOpts, locale: string): string => {
    // If we add languages that have different translation rules, add a new
    // translation strategy. Right now all we have is the one for English.

    const strategy = translationStrategies.default;
    return strategy(key, opts, locale);
  },
  warn: (message: string) => {
    if (__DEV__) {
      console.error(message);
    }
  },
});

polyglot.extend(window.Greenhouse.Translation.fallbackTranslations);
polyglot.extend(window.Greenhouse.Translation.translations);
window.polyglot = polyglot;

const translationStrategies: {
  [name: string]: onMissingKeyType,
} = {
  /* $FlowFixMe -> in normal usage, this will always return a string, but if
     the dev hasn't added translations yet it will warn/void. Flow doesn't get this
  */
  default: (key: string, opts?: LocalizeOpts, locale?: string): string => {
    /*
      If this function is called, we are in one of two states:
        1) The translation is missing and we should call a warn function
        2) A user called t('foo'), where 'foo' does not exist but where
           'foo.one' and 'foo.other' exist and refer to pluralization strings
    */

    const phraseForOne: ?string = polyglot.phrases[`${key}.one`];
    const phraseForOther: ?string = polyglot.phrases[`${key}.other`];

    if (phraseForOne && phraseForOther) {
      if (opts && opts.count === 1) {
        return Polyglot.transformPhrase(phraseForOne, opts, locale);
      } else {
        return Polyglot.transformPhrase(phraseForOther, opts, locale);
      }
    } else {
      polyglot.warn(`Missing translation for key: ${key}`);
    }
  },
};

const tList = (
  key: string,
  params?: LocalizeOpts,
  skipFirstIfEmpty: boolean = false
): Array<string> => {
  let phrases = [];
  let i = 0;
  let phrase = polyglot.phrases[`${key}.${i}`];

  const getNextPhrase = index => polyglot.phrases[`${key}.${index}`];
  // support lists where the first value is empty (e.g. month name translations)
  if (!phrase && skipFirstIfEmpty) {
    phrase = getNextPhrase(++i);
  }

  if (phrase) {
    while (phrase) {
      let translation = Polyglot.transformPhrase(phrase, params);

      if (window.Greenhouse.Translation.spooky && translation) {
        translation = pseudolocalize(translation);
      }

      phrases.push(translation);
      phrase = getNextPhrase(++i);
    }
  } else {
    polyglot.warn(`Translation key is not a list: ${key}, use t()`);
  }

  return phrases;
};

const t = (phrase: string, params?: LocalizeOpts): string => {
  const translatedString = polyglot.t(phrase, params);

  if (window.Greenhouse.Translation.spooky && translatedString) {
    return pseudolocalize(translatedString);
  } else {
    return translatedString;
  }
};

const withoutPseudolocalization = polyglot.t.bind(polyglot);

const withLocalePrefix = (localePrefix: string) => (
  phrase: string,
  params?: PolyglotOpts
): string => t(`${localePrefix}.${phrase}`, params);

const localizeNumber = (number: number): string =>
  number.toLocaleString(window.Greenhouse.Translation.locale);

export default t;

export {
  polyglot,
  withLocalePrefix,
  withoutPseudolocalization,
  localizeNumber,
  tList,
};
